import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Send, Zap } from "lucide-react";
import { Toaster, toast } from "../components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!EMAIL_PATTERN.test(email.trim())) {
      setError("Enter a valid email address.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSuccess(true);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 relative overflow-hidden" style={{ background: "var(--surface-page)" }}>
      <Toaster richColors />
      {["var(--accent-gold)", "var(--accent-gold)", "var(--ce-08-valor)", "var(--ce-05-wonder)"].map((color, index) => (
        <motion.div
          key={color}
          className="absolute rounded-full opacity-20"
          style={{
            width: 140 - index * 18,
            height: 140 - index * 18,
            background: `radial-gradient(circle, ${color}, transparent)`,
            left: `${10 + index * 20}%`,
            top: `${12 + index * 14}%`,
          }}
          animate={{ y: [0, -24, 0], x: [0, 16, 0] }}
          transition={{ duration: 4 + index, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="rounded-2xl p-8" style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, var(--accent-gold), var(--accent-gold))" }}>
              <Zap size={20} />
            </div>
            <div>
              <h1 className="font-black text-xl">ArcScript AI</h1>
              <p className="text-[var(--ink-tertiary)] text-sm">Recover your studio access</p>
            </div>
          </div>

          <h2 className="text-3xl font-black mb-2">Forgot your password?</h2>
          <p className="text-[var(--ink-secondary)] text-sm leading-relaxed mb-8">
            Enter the email tied to your account and we’ll send you a secure link to reset it.
          </p>

          {success ? (
            <div className="space-y-6">
              <div
                className="rounded-xl px-4 py-4 text-sm leading-relaxed"
                style={{ background: "rgba(122, 144, 112, 0.12)", border: "1px solid rgba(122, 144, 112, 0.35)", color: "var(--ink-primary)" }}
                data-testid="forgot-password-success"
              >
                If an account exists with that email, a reset link has been sent. Check your inbox.
              </div>
              <Link
                to="/auth"
                data-testid="forgot-password-back"
                className="inline-flex items-center gap-2 text-sm font-medium text-[var(--accent-gold)] hover:text-[var(--accent-gold-hover)] transition-colors"
              >
                <ArrowLeft size={14} />
                Back to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-[var(--ink-secondary)] text-sm mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ink-tertiary)]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="writer@studio.com"
                    data-testid="forgot-password-email"
                    className="w-full pl-11 pr-4 py-3 rounded-xl placeholder-[var(--ink-tertiary)] text-sm outline-none transition-colors"
                    style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }}
                  />
                </div>
                {error && <p className="text-[var(--semantic-error)] text-xs mt-2">{error}</p>}
              </div>

              <motion.button
                type="submit"
                disabled={loading}
                data-testid="forgot-password-submit"
                whileHover={{ scale: loading ? 1 : 1.01 }}
                whileTap={{ scale: loading ? 1 : 0.99 }}
                className="w-full py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2"
                style={{ background: loading ? "var(--surface-raised)" : "var(--accent-gold)" }}
              >
                {loading ? (
                  <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  <>
                    <Send size={16} />
                    Send Reset Link
                  </>
                )}
              </motion.button>

              <Link
                to="/auth"
                data-testid="forgot-password-back"
                className="inline-flex items-center gap-2 text-sm text-[var(--ink-secondary)] hover: transition-colors"
              >
                <ArrowLeft size={14} />
                Back to Login
              </Link>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}